// src/components/purchases/SerializedFromCatalogModal.tsx
// Modal for creating serialized inventory items from a purchase line item
// Pre-populated from product catalog - only asks for serial numbers per item
// Updated: Added body armor/expiration tracking fields for categories that require it

import React, { useState, useEffect } from 'react';
import { X, Package, AlertTriangle, Plus, Trash2, CheckCircle, Shield } from 'lucide-react';
import { Product } from '../../services/productCatalogAPI';
import { fulfillmentAPI, SerializedItemInput } from '../../services/fulfillmentAPI';
import { PurchaseLineItem } from '../../types/purchase';
import { tenantConfigAPI } from '../../services/tenantConfigAPI';
import { categoryAPI } from '../../services/categoryAPI';
import { requiresExpiration, getDefaultExpirationMonths, calculateExpirationDate } from '../../utils/categoryTracking';
import { CategoryHierarchy } from '../../types/category';

interface SerializedFromCatalogModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: Product;
  lineItem: PurchaseLineItem;
  purchaseId: string;
  onComplete: (inventoryIds: string[]) => void;
}

interface ItemEntry {
  id: string;
  serialNumber: string;
  condition: 'EXCELLENT' | 'GOOD' | 'FAIR' | 'POOR' | 'NEEDS_REPAIR';
  location: string;
  subLocation: string;
  notes: string;
  // Body armor fields
  ballisticLevel: string;
  size: string;
  expirationDate: string;
}

const CONDITION_OPTIONS: { value: ItemEntry['condition']; label: string }[] = [
  { value: 'EXCELLENT', label: 'Excellent' },
  { value: 'GOOD', label: 'Good' },
  { value: 'FAIR', label: 'Fair' },
  { value: 'POOR', label: 'Poor' },
  { value: 'NEEDS_REPAIR', label: 'Needs Repair' }
];

const BALLISTIC_LEVELS = ['IIA', 'II', 'IIIA', 'III', 'IV'];
const SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL'];

const SerializedFromCatalogModal: React.FC<SerializedFromCatalogModalProps> = ({
  isOpen,
  onClose,
  product,
  lineItem,
  purchaseId,
  onComplete
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Remaining quantity to fulfill
  const remainingQuantity = lineItem.quantity - lineItem.allocatedQuantity;
  
  // Item entries - one row per serialized item
  const [items, setItems] = useState<ItemEntry[]>([]);
  
  // Default values for new items
  const [defaultLocation, setDefaultLocation] = useState(product.defaultLocation || '');
  const [defaultCondition, setDefaultCondition] = useState<ItemEntry['condition']>('EXCELLENT');
  
  // Body armor defaults
  const [defaultBallisticLevel, setDefaultBallisticLevel] = useState('IIIA');
  const [defaultSize, setDefaultSize] = useState('L');
  const [defaultExpirationDate, setDefaultExpirationDate] = useState('');
  
  // Storage locations from tenant config
  const [storageLocations, setStorageLocations] = useState<string[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(true);
  
  // Category info for expiration tracking
  const [subcategory, setSubcategory] = useState<CategoryHierarchy | null>(null);
  const [categoryLoading, setCategoryLoading] = useState(true);
  const [needsExpiration, setNeedsExpiration] = useState(false);

  // Load storage locations on mount
  useEffect(() => {
    const loadLocations = async () => {
      try {
        const locations = await tenantConfigAPI.getInventoryLocations();
        setStorageLocations(locations);
      } catch (err) {
        console.error('Failed to load storage locations:', err);
      } finally {
        setLocationsLoading(false);
      }
    };
    loadLocations();
  }, []);

  // Load category to check expiration requirement
  useEffect(() => {
    const loadCategory = async () => {
      if (!lineItem.subcategoryId) {
        setCategoryLoading(false);
        return;
      }
      
      try {
        const response = await categoryAPI.getAllCategories();
        if (response.success && response.data) {
          // Find the subcategory
          const allCategories = response.data.categories;
          const found = allCategories.find(c => c.categoryId === lineItem.subcategoryId);
          if (found) {
            setSubcategory(found as CategoryHierarchy);
            const needsExp = requiresExpiration(found as CategoryHierarchy);
            setNeedsExpiration(needsExp);
            
            // Set default expiration date if configured
            if (needsExp) {
              const defaultMonths = getDefaultExpirationMonths(found as CategoryHierarchy);
              if (defaultMonths) {
                const expDate = calculateExpirationDate(new Date(), defaultMonths);
                setDefaultExpirationDate(expDate);
              }
            }
          }
        }
      } catch (err) {
        console.error('Failed to load category:', err);
      } finally {
        setCategoryLoading(false);
      }
    };
    
    if (isOpen) {
      loadCategory();
    }
  }, [isOpen, lineItem.subcategoryId]);

  // Initialize with one empty item when modal opens
  useEffect(() => {
    if (isOpen && !categoryLoading) {
      setDefaultLocation(product.defaultLocation || '');
      setDefaultCondition('EXCELLENT');
      setError(null);
      // Start with one empty entry
      setItems([createEmptyItem(product.defaultLocation || '', 'EXCELLENT')]);
    }
  }, [isOpen, product, categoryLoading]);

  const createEmptyItem = (location: string, condition: ItemEntry['condition']): ItemEntry => ({
    id: crypto.randomUUID(),
    serialNumber: '',
    condition,
    location,
    subLocation: '',
    notes: '',
    ballisticLevel: defaultBallisticLevel,
    size: defaultSize,
    expirationDate: defaultExpirationDate
  });

  const handleAddItem = () => {
    if (items.length >= remainingQuantity) {
      setError(`Cannot add more than ${remainingQuantity} items (remaining on line item)`);
      return;
    }
    setItems([...items, createEmptyItem(defaultLocation, defaultCondition)]);
  };

  const handleRemoveItem = (id: string) => {
    if (items.length <= 1) return; // Keep at least one
    setItems(items.filter(item => item.id !== id));
  };

  const handleItemChange = (id: string, field: keyof ItemEntry, value: string) => {
    setItems(items.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const handleApplyDefaultsToAll = () => {
    setItems(items.map(item => ({
      ...item,
      location: defaultLocation,
      condition: defaultCondition,
      ...(needsExpiration && {
        ballisticLevel: defaultBallisticLevel,
        size: defaultSize,
        expirationDate: defaultExpirationDate
      })
    })));
  };

  const validateItems = (): string | null => {
    // Check for empty serial numbers
    const emptySerials = items.filter(item => !item.serialNumber.trim());
    if (emptySerials.length > 0) {
      return 'All items must have a serial number';
    }

    // Check for duplicate serial numbers
    const serials = items.map(item => item.serialNumber.trim().toUpperCase());
    const duplicates = serials.filter((serial, index) => serials.indexOf(serial) !== index);
    if (duplicates.length > 0) {
      return `Duplicate serial numbers found: ${Array.from(new Set(duplicates)).join(', ')}`;
    }

    // Check for missing locations
    const noLocation = items.filter(item => !item.location.trim());
    if (noLocation.length > 0) {
      return 'All items must have a storage location';
    }

    // Body armor validation
    if (needsExpiration) {
      const missingBallistic = items.filter(item => !item.ballisticLevel);
      if (missingBallistic.length > 0) {
        return 'All items must have a ballistic level';
      }
      
      const missingSize = items.filter(item => !item.size);
      if (missingSize.length > 0) {
        return 'All items must have a size';
      }
      
      const missingExpiration = items.filter(item => !item.expirationDate);
      if (missingExpiration.length > 0) {
        return 'All items must have an expiration date';
      }
    }

    return null;
  };

  const handleSubmit = async () => {
    const validationError = validateItems();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Transform items to API format
      const serializedItems: SerializedItemInput[] = items.map(item => {
        const baseItem: SerializedItemInput = {
          serialNumber: item.serialNumber.trim(),
          condition: item.condition,
          location: item.location.trim(),
          subLocation: item.subLocation.trim() || undefined,
          notes: item.notes.trim() || undefined
        };
        
        // Add body armor fields if category requires expiration
        if (needsExpiration) {
          baseItem.bodyArmor = {
            ballisticLevel: item.ballisticLevel,
            size: item.size,
            expirationDate: item.expirationDate
          };
        }
        
        return baseItem;
      });

      const response = await fulfillmentAPI.createSerializedInstances({
        purchaseId,
        lineItemId: lineItem.lineItemId,
        productId: product.productId,
        items: serializedItems,
        categoryName: lineItem.categoryName,
        subcategoryName: lineItem.subcategoryName,
        categoryId: lineItem.categoryId,
        subcategoryId: lineItem.subcategoryId
      });

      onComplete(response.inventoryIds);
      onClose();
    } catch (err: any) {
      console.error('Create serialized items failed:', err);
      setError(err.message || 'Failed to create serialized items');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const canAddMore = items.length < remainingQuantity;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center space-x-2">
            <Package className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">Add Serialized Items</h3>
            {needsExpiration && (
              <span className="flex items-center space-x-1 px-2 py-0.5 bg-amber-100 text-amber-800 text-xs rounded-full">
                <Shield className="w-3 h-3" />
                <span>Expiration Tracked</span>
              </span>
            )}
          </div>
          <button 
            onClick={onClose} 
            className="p-1 hover:bg-gray-100 rounded"
            disabled={loading}
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="p-4 space-y-4 overflow-y-auto flex-1">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-start space-x-2">
              <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {/* Product Info (read-only) */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Package className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-medium text-gray-900">{product.name}</h4>
                {(product.manufacturer || product.model) && (
                  <p className="text-sm text-gray-600">
                    {[product.manufacturer, product.model].filter(Boolean).join(' • ')}
                  </p>
                )}
                <div className="mt-2 flex items-center space-x-4 text-sm">
                  <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                    Serialized Item
                  </span>
                  <span className="text-gray-600">
                    Remaining to add: <span className="font-medium text-gray-900">{remainingQuantity}</span>
                  </span>
                  <span className="text-gray-600">
                    Adding: <span className="font-medium text-gray-900">{items.length}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Default Settings */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Default Values for New Items</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Default Location</label>
                <select
                  value={defaultLocation}
                  onChange={(e) => setDefaultLocation(e.target.value)}
                  disabled={locationsLoading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                >
                  <option value="">Select location...</option>
                  {storageLocations.map((loc) => (
                    <option key={loc} value={loc}>{loc}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Default Condition</label>
                <select
                  value={defaultCondition}
                  onChange={(e) => setDefaultCondition(e.target.value as ItemEntry['condition'])}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                >
                  {CONDITION_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={handleApplyDefaultsToAll}
                  className="px-3 py-2 text-sm text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                >
                  Apply to All Items
                </button>
              </div>
            </div>
            
            {/* Body Armor Default Settings */}
            {needsExpiration && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <h5 className="text-xs font-medium text-amber-700 mb-3 flex items-center">
                  <Shield className="w-3 h-3 mr-1" />
                  Body Armor Defaults
                </h5>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Default Ballistic Level</label>
                    <select
                      value={defaultBallisticLevel}
                      onChange={(e) => setDefaultBallisticLevel(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    >
                      {BALLISTIC_LEVELS.map((level) => (
                        <option key={level} value={level}>{level}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Default Size</label>
                    <select
                      value={defaultSize}
                      onChange={(e) => setDefaultSize(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    >
                      {SIZES.map((size) => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Default Expiration Date</label>
                    <input
                      type="date"
                      value={defaultExpirationDate}
                      onChange={(e) => setDefaultExpirationDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Item Entry Table */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8">
                      #
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Serial Number <span className="text-red-500">*</span>
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Location <span className="text-red-500">*</span>
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Condition
                    </th>
                    {needsExpiration && (
                      <>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Level <span className="text-red-500">*</span>
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Size <span className="text-red-500">*</span>
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Expires <span className="text-red-500">*</span>
                        </th>
                      </>
                    )}
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {items.map((item, index) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-sm text-gray-500">
                        {index + 1}
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          value={item.serialNumber}
                          onChange={(e) => handleItemChange(item.id, 'serialNumber', e.target.value)}
                          placeholder="Enter serial number"
                          className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <select
                          value={item.location}
                          onChange={(e) => handleItemChange(item.id, 'location', e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        >
                          <option value="">Select...</option>
                          {storageLocations.map((loc) => (
                            <option key={loc} value={loc}>{loc}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <select
                          value={item.condition}
                          onChange={(e) => handleItemChange(item.id, 'condition', e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        >
                          {CONDITION_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </td>
                      {needsExpiration && (
                        <>
                          <td className="px-3 py-2">
                            <select
                              value={item.ballisticLevel}
                              onChange={(e) => handleItemChange(item.id, 'ballisticLevel', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            >
                              <option value="">Select...</option>
                              {BALLISTIC_LEVELS.map((level) => (
                                <option key={level} value={level}>{level}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            <select
                              value={item.size}
                              onChange={(e) => handleItemChange(item.id, 'size', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            >
                              <option value="">Select...</option>
                              {SIZES.map((size) => (
                                <option key={size} value={size}>{size}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="date"
                              value={item.expirationDate}
                              onChange={(e) => handleItemChange(item.id, 'expirationDate', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            />
                          </td>
                        </>
                      )}
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(item.id)}
                          disabled={items.length <= 1}
                          className="p-1 text-gray-400 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Add More Button */}
          <div className="flex justify-between items-center">
            <button
              type="button"
              onClick={handleAddItem}
              disabled={!canAddMore}
              className="flex items-center space-x-2 px-3 py-2 text-sm text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-4 h-4" />
              <span>Add Another Item</span>
            </button>
            {!canAddMore && (
              <span className="text-sm text-amber-600">
                Maximum items for this line item reached
              </span>
            )}
          </div>

          {/* Summary */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <p className="text-sm text-green-800">
              <CheckCircle className="w-4 h-4 inline mr-1" />
              <span className="font-medium">{items.length}</span> serialized item{items.length !== 1 ? 's' : ''} will be created in inventory from product "{product.name}".
              {needsExpiration && (
                <span className="ml-1 text-amber-700">
                  (with expiration tracking)
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || items.length === 0 || categoryLoading}
            className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors text-sm disabled:opacity-50 flex items-center space-x-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Creating...</span>
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" />
                <span>Create {items.length} Item{items.length !== 1 ? 's' : ''}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SerializedFromCatalogModal;
